"use client"

import { useState, useEffect } from "react"
import { useAuth } from "../../context/AuthContext"
import { useLanguage } from "../../context/LanguageContext"
import api from "../../utils/api"
import toast from "react-hot-toast"
import { User, CreditCard, BarChart3, Edit } from "lucide-react"
import EnhancedDonationButton from "./EnhancedDonationButton"
import EditProfile from "./EditProfile"
import DonationHistory from "./DonationHistory"
import MonthlyReport from "./MonthlyReport"
import WhatsAppCommunity from "./WhatsAppCommunity"

const UserDashboard = () => {
  const { user } = useAuth()
  const { t } = useLanguage()
  const [activeTab, setActiveTab] = useState("overview")
  const [donations, setDonations] = useState([])
  const [monthlyData, setMonthlyData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchDashboardData()
  }, [])

  const fetchDashboardData = async () => {
    try {
      const [donationsRes, monthlyRes] = await Promise.all([
        api.get("/donations/history"),
        api.get("/donations/monthly-summary"),
      ])

      setDonations(donationsRes.data.donations)
      setMonthlyData(monthlyRes.data)
    } catch (error) {
      toast.error("Failed to fetch dashboard data")
    } finally {
      setLoading(false)
    }
  }

  const completedDonations = donations.filter((d) => d.status === "completed")
  const totalDonated = completedDonations.reduce((sum, d) => sum + Number.parseFloat(d.amount), 0)

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">{t("common.loading")}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        {/* Welcome Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {t("dashboard.welcome")}, {user.name}!
          </h1>
          <p className="text-gray-600">{t("dashboard.thankYou")}</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <CreditCard className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">{t("dashboard.totalDonations")}</p>
                <p className="text-2xl font-bold text-gray-900">{completedDonations.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <BarChart3 className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">{t("dashboard.totalAmount")}</p>
                <p className="text-2xl font-bold text-gray-900">₹{totalDonated}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <EnhancedDonationButton onSuccess={fetchDashboardData} />
          </div>
        </div>

        {/* WhatsApp Community Section */}
        <div className="mb-8">
          <WhatsAppCommunity />
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white rounded-lg shadow-md mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6">
              {[
                { id: "overview", name: t("dashboard.overview"), icon: User },
                { id: "profile", name: t("dashboard.editProfile"), icon: Edit },
                { id: "history", name: t("dashboard.donationHistory"), icon: CreditCard },
                { id: "reports", name: t("dashboard.monthlyReports"), icon: BarChart3 },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`${
                    activeTab === tab.id
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
                >
                  <tab.icon size={18} />
                  <span>{tab.name}</span>
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {activeTab === "overview" && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">{t("dashboard.profileInfo")}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-600">{t("dashboard.email")}</p>
                      <p className="text-gray-900">{user.email}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">{t("dashboard.phone")}</p>
                      <p className="text-gray-900">{user.phone}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">{t("dashboard.dateOfBirth")}</p>
                      <p className="text-gray-900">{new Date(user.date_of_birth).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">{t("dashboard.address")}</p>
                      <p className="text-gray-900">{user.address}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "profile" && <EditProfile />}

            {activeTab === "history" && <DonationHistory donations={donations} />}

            {activeTab === "reports" && monthlyData && <MonthlyReport data={monthlyData} />}
          </div>
        </div>
      </div>
    </div>
  )
}

export default UserDashboard
